import { type QueryClient, type QueryKey, replaceEqualDeep } from '@tanstack/react-query';
import { _bumpPerf } from '../../devtool/perf-counters.js';

let generation = 0;
const completed = new WeakMap<object, number>();
const snapshots = new WeakMap<object, number>();

// REST and generated realtime refreshes must use the same clock, including
// requests started in the same millisecond by separate hook instances.
export function nextFeedRefreshGeneration(): number {
  return ++generation;
}

export function acceptFeedRefresh(
  client: QueryClient,
  key: QueryKey,
  refreshGeneration: number,
): boolean {
  const query = client.getQueryCache().find({ queryKey: key, exact: true });
  if (query === undefined) return true;
  if (refreshGeneration < (completed.get(query) ?? 0)) {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('normalized.staleFeedRefreshes');
    return false;
  }
  completed.set(query, refreshGeneration);
  return true;
}

export function markFeedRefreshSnapshot<T>(snapshot: T, refreshGeneration: number): T {
  if (snapshot !== null && typeof snapshot === 'object') {
    snapshots.set(snapshot, refreshGeneration);
  }
  return snapshot;
}

// TanStack commits after the async query wrapper returns. Recheck at that
// boundary so a realtime completion in between cannot be overwritten.
export function shareFeedRefreshSnapshot(
  client: QueryClient,
  key: QueryKey,
  previous: unknown,
  next: unknown,
  share?: (previous: unknown, next: unknown) => unknown,
): unknown {
  const refreshGeneration =
    next !== null && typeof next === 'object' ? snapshots.get(next) : undefined;
  if (refreshGeneration !== undefined && !acceptFeedRefresh(client, key, refreshGeneration)) {
    return previous;
  }
  return (share ?? replaceEqualDeep)(previous, next);
}
